'use client';

import React, { useState } from 'react';

import { usePathname } from 'next/navigation';

import { Button } from '@repo/ui/button';
import Icon from '@repo/ui/icon';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from '@repo/ui/navigation-menu';
import { Sheet, SheetContent, SheetTrigger } from '@repo/ui/sheet';

import { toClassName } from 'helpers/format';

import { SocialsSettings } from 'types/sanity.types';

import { useTranslation } from 'hooks/use-translation';

import Image from 'components/nextjs-fixes/image';
import Link from 'components/nextjs-fixes/link';

import { styles } from '.';

type TProps = {
  logoUrl?: string;
  routes: {
    id: string;
    label: string;
    url: string;
    subroutes?: { id: string; label: string; url: string }[];
  }[];
  socials?: SocialsSettings[];
  locale: string;
};

const SideMenu = (props: TProps) => {
  const { logoUrl, locale, routes, socials } = props;

  const [isOpen, setIsOpen] = useState(false);

  const pathname = usePathname();

  const { t } = useTranslation(locale);

  return (
    <div className={styles.container}>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger aria-label="hamburger">
          <Icon accessor="bars" />
        </SheetTrigger>
        <SheetContent side="left" className="w-[90vw] !p-0 !border-0">
          <div className={styles.sheet.container}>
            <div className={styles.sheet.content}>
              <div
                className={styles.sheet.head}
                onClick={() => setIsOpen(false)}
              >
                <Link href="/">
                  <Image
                    src={logoUrl}
                    width={170}
                    height={50}
                    alt="logo"
                    className={styles.image}
                  />
                </Link>
              </div>

              <div className={styles.sheet.body}>
                <NavigationMenu className="!items-start !justify-start">
                  <NavigationMenuList className={styles.sheet.list}>
                    {routes.map((route) => (
                      <NavigationMenuItem
                        key={route.id}
                        onClick={() => setIsOpen(false)}
                      >
                        <Link href={route.url} legacyBehavior passHref>
                          <NavigationMenuLink
                            className={toClassName(
                              ' ',
                              pathname.includes(route.url) && 'opacity-20',
                            )}
                          >
                            {route.label}
                          </NavigationMenuLink>
                        </Link>
                      </NavigationMenuItem>
                    ))}
                    <NavigationMenuItem onClick={() => setIsOpen(false)}>
                      <Link href="/tiny-houses" legacyBehavior passHref>
                        <NavigationMenuLink
                          className={toClassName(
                            // navigationMenuTriggerStyle(),
                            '!text-sm !  !text-[#29AAC7]',
                            pathname.includes('/tiny-houses') && '!opacity-20',
                          )}
                        >
                          {t('component.navbar.label.tinyHouses')}
                        </NavigationMenuLink>
                      </Link>
                    </NavigationMenuItem>
                  </NavigationMenuList>
                </NavigationMenu>
              </div>

              <div className={styles.sheet.foot}>
                {/* <LanguageSection lang={lang} /> */}
              </div>
            </div>

            <div className="text-sm">
              <div className="bg-[#E84F31] p-8">
                <Link href={`/${locale}/contact`}>
                  <Button
                    className={styles.button}
                    onClick={() => setIsOpen(false)}
                  >
                    {t('component.footer.label.getInContact')}
                  </Button>
                </Link>
              </div>
              <div className="bg-[#2C4146] p-8 text-white">
                <ul className={styles.list}>
                  {socials?.map((social) => (
                    <li key={social._id}>
                      <a
                        href={social.button?.link?.url}
                        target="_blank"
                        aria-label={social.button?.icon?.accessor}
                        rel="noreferrer"
                      >
                        <Icon
                          type={social.button?.icon?.type}
                          accessor={social.button?.icon?.accessor}
                        />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default SideMenu;
